import type { SortPref, SortScope } from './default-sort-pref';
import type { GalleryState } from './state';
import { galleryLinkFor, pathToTab, searchParamsToGalleryState } from './url-state';

/**
 * RÉSOLUTION DU TRI — source UNIQUE de « URL (+ préférences + graine du compte) → état ».
 *
 * `url-state.ts` est volontairement PUR et ne connaît que la barre d'adresse : sans `tri`
 * il retombe sur le repli neutre (`takenAt`). Or le VRAI défaut d'une vue est la
 * PRÉFÉRENCE utilisateur (`default-sort-pref.ts`, souvent « aléatoire »), et l'ordre
 * aléatoire a besoin d'une GRAINE (`md5(seed || id)` côté serveur : sans graine, l'ordre
 * dégénère en `md5('' || id)` — figé, identique pour tout le monde et non re-mélangeable).
 *
 * Cette dernière étape était DUPLIQUÉE et DIVERGENTE : `GalleryView` appliquait préférence
 * + graine, `ViewerRoute` non (il enchaînait `searchParamsToGalleryState` → `stateToFilters`
 * sur `back ?? '/'`). Les deux décrivaient alors des ensembles ORDONNÉS différents et
 * préc./suiv. sautait à côté (variantes v0.53.0 et v1.21.6). Tout passe désormais par ici.
 *
 * Le module reste PUR : les préférences et la graine du compte sont INJECTÉES (aucun accès
 * à `localStorage` ni à `useAuth`), donc entièrement testable pour lui-même.
 *
 * DEUX préférences, choisies par le CHEMIN (`sortScopeFor`) : l'accueil (`/`) a la sienne,
 * tout le reste (`/dossiers`, `/tags`) partage le tri « collant » de NAVIGATION. La
 * justification de l'exception est dans `default-sort-pref.ts`.
 *
 * Les quatre chemins, dans l'ordre de priorité :
 *
 *  1. `tri=<clé>` dans l'URL (y compris `tri=random`) ⇒ tri EXPLICITE, jamais écrasé par
 *     la préférence. C'est le correctif v1.21.3 : une URL indiscernable d'une arrivée à
 *     froid se faisait réécraser par la préférence dans le même rendu (choisir « Date de
 *     prise de vue » avec une préférence « Aléatoire » était inopérant).
 *  2. `graine=<seed>` sans `tri` ⇒ URL LEGACY (avant le LOT C1, l'aléatoire se sérialisait
 *     par sa graine) : aléatoire explicite, en HONORANT la graine portée — un favori ou un
 *     lien déjà partagé rend exactement le même ordre qu'avant.
 *  3. sinon ⇒ la PRÉFÉRENCE de la portée du chemin, critère ET sens.
 *  4. dans TOUS les cas où le tri résolu est `random` sans graine explicite ⇒ la graine du
 *     COMPTE (persistée serveur, `CurrentUser.randomSeed`) — c'est ce qui permet de RETIRER
 *     `?graine=` de la barre d'adresse sans perdre l'ordre.
 */

/** Entrées EXTERNES de la résolution (injectées → module pur). */
export interface SortResolutionContext {
  /** Préférence de tri de l'ACCUEIL (`getSortPref('home')` chez l'appelant). */
  homePref: SortPref;
  /** Préférence de tri de NAVIGATION (`getSortPref('browse')` chez l'appelant). */
  browsePref: SortPref;
  /** Graine d'aléatoire du COMPTE (`user.randomSeed`, ou repli `accountRandomSeed`). */
  accountSeed: string;
}

/**
 * Emplacement de préférence d'un chemin de galerie : l'ACCUEIL (`/`, onglet Galerie) a le
 * sien, tout le reste (`/dossiers`, `/tags`) partage celui de NAVIGATION.
 *
 * Bâti sur `pathToTab` pour hériter de sa normalisation (barre oblique finale) et de son
 * repli : un chemin inconnu compte comme l'accueil, exactement comme il compte déjà comme
 * l'onglet Galerie.
 */
export function sortScopeFor(pathname: string): SortScope {
  return pathToTab(pathname) === 'gallery' ? 'home' : 'browse';
}

/** Préférence applicable à un chemin (accueil ou navigation). */
function prefFor(pathname: string, ctx: SortResolutionContext): SortPref {
  return sortScopeFor(pathname) === 'home' ? ctx.homePref : ctx.browsePref;
}

/**
 * État de galerie EFFECTIF pour un chemin + une query (cf. les quatre chemins ci-dessus).
 * `pathname` porte l'onglet (`/`, `/dossiers`, `/tags`), `search` le reste des filtres.
 */
export function resolveGalleryState(
  pathname: string,
  search: URLSearchParams,
  ctx: SortResolutionContext,
): GalleryState {
  const derived = searchParamsToGalleryState(pathname, search);
  // « L'URL porte-t-elle un choix de tri ? » se lit sur la PRÉSENCE des clés, pas sur la
  // valeur résolue : `tri=takenAt` vaut le repli neutre de `url-state` et serait
  // indiscernable d'une absence de clé — exactement la régression v1.21.3.
  const explicit = search.has('tri') || search.has('graine');
  const pref = prefFor(pathname, ctx);
  const state: GalleryState = explicit
    ? derived
    : { ...derived, sort: pref.sort, dir: pref.dir, seed: undefined };
  // Aléatoire sans graine portée (cas nominal depuis le LOT C1) ⇒ graine du COMPTE.
  return state.sort === 'random' && !state.seed ? { ...state, seed: ctx.accountSeed } : state;
}

/**
 * Même résolution à partir d'un HREF complet (`/dossiers?dossier=2024`) — la forme sous
 * laquelle circule l'URL de retour de la visionneuse (`location.state.back`). Un href sans
 * query, `/` compris, est parfaitement valide : la préférence et la graine du compte
 * suffisent alors à reconstituer le MÊME ensemble ordonné que la grille.
 */
export function resolveGalleryHref(href: string, ctx: SortResolutionContext): GalleryState {
  const [path, query = ''] = href.split('?');
  return resolveGalleryState(path ?? '/', new URLSearchParams(query), ctx);
}

/**
 * Lien de galerie MINIMAL : le lien complet (`galleryLinkFor`), dépouillé de `tri`/`ordre`
 * QUAND ces clés sont redondantes avec la préférence du contexte d'ARRIVÉE.
 *
 * POURQUOI il faut ce filet plutôt qu'une simple omission dans `url-state` : celui-ci
 * sérialise TOUJOURS `tri=` (lire son long commentaire avant d'y toucher). Une URL sans
 * marqueur de tri signifie « applique la préférence » ; omettre `tri` « pour faire propre »
 * a déjà fait perdre le choix de l'utilisateur — c'est la régression v1.21.3, qu'on ne
 * rouvre pas. Mais le TOUJOURS a un effet non désiré : l'accueil est propre (`/`) et
 * devient `/?tri=random&q=chat` à la première recherche.
 *
 * La sortie de ce dilemme est un POINT FIXE VÉRIFIÉ, pas une devinette : on construit la
 * version courte, on la RÉSOUT avec ce module, et on ne la garde que si le tri et le sens
 * résolus sont EXACTEMENT ceux visés. Le contrôle porte sur l'href CIBLE, donc sur la
 * préférence du contexte d'arrivée (aller de `/` vers `/dossiers` interroge bien la
 * préférence de NAVIGATION). Un tri qui diverge du défaut de ce contexte reste donc
 * TOUJOURS écrit en toutes lettres.
 *
 * En ALÉATOIRE le SENS n'est pas comparé : il n'a pas de sens pour un ordre pseudo-uniforme
 * (piloté par la graine), et `galleryLinkFor` n'émet alors jamais `ordre` — la version
 * LONGUE ne le préserverait pas davantage.
 */
export function minimalGalleryLink(state: GalleryState, ctx: SortResolutionContext): string {
  const full = galleryLinkFor(state);
  const [path = '/', query = ''] = full.split('?');
  const stripped = new URLSearchParams(query);
  stripped.delete('tri');
  stripped.delete('ordre');
  const rest = stripped.toString();
  const short = rest === '' ? path : `${path}?${rest}`;
  const resolved = resolveGalleryHref(short, ctx);
  const sameSort = resolved.sort === state.sort;
  const sameDir = state.sort === 'random' || resolved.dir === state.dir;
  return sameSort && sameDir ? short : full;
}
